'use client';

import React, { useEffect, useState } from 'react';
import { cn } from '@/utils/сn';
import { IconButton } from '../buttons/IconButton';
import ModalPortal from './ModalPortal';
import { type ModalContentVariantsProps, modalContentStyles } from './styles';
import CancelIcon from '/public/icons/cross.svg';

type ModalProps = {
  open: boolean;
  onClose: () => void;
  hideCloseModalIcon?: boolean;
  variant?: ModalContentVariantsProps['variant'];
  contentClassName?: string;
} & React.PropsWithChildren;

const SHEET_EXIT_DURATION_MS = 220;

export const Modal = ({
  open,
  onClose,
  children,
  hideCloseModalIcon = false,
  variant,
  contentClassName,
}: ModalProps) => {
  const isSheet = variant === 'bottomSheet';
  const [isExiting, setIsExiting] = useState(false);
  const [isSheetRendered, setIsSheetRendered] = useState(open);

  useEffect(() => {
    if (!isSheet) return;

    if (open) {
      setIsSheetRendered(true);
      setIsExiting(false);
      return;
    }

    setIsExiting(true);
    const timeout = setTimeout(() => {
      setIsSheetRendered(false);
      setIsExiting(false);
    }, SHEET_EXIT_DURATION_MS);

    return () => clearTimeout(timeout);
  }, [open, isSheet]);

  if (isSheet ? !isSheetRendered : !open) return null;

  return (
    <ModalPortal>
      <dialog
        className={cn(
          'fixed inset-0 w-screen h-screen z-50 flex items-center justify-center bg-[#2125298F] backdrop-blur-sm',
          variant === 'bottomSheet' && 'max-md:items-end'
        )}
      >
        <div
          className={cn(
            modalContentStyles({ variant }),
            contentClassName,
            isSheet && (isExiting ? 'max-md:animate-sheet-out' : 'max-md:animate-sheet-in')
          )}
          id="modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          {!hideCloseModalIcon && (
            <header className={cn('sticky top-0 z-10 h-0', variant === 'fullScreenMobile' && 'max-md:hidden')}>
              <div className="absolute top-2 right-2 max-md:top-4 max-md:right-4 max-md:[&>button]:h-8 max-md:[&>button]:w-8">
                <IconButton
                  size="small"
                  aria-label="Close"
                  icon={<CancelIcon className="w-4 h-4" />}
                  onClick={onClose}
                  color="default"
                />
              </div>
            </header>
          )}
          {variant === 'bottomSheet' && (
            <div className="absolute inset-x-0 top-4 hidden justify-center max-md:flex" aria-hidden="true">
              <span className="h-1.5 w-11 rounded-full bg-grey-200" />
            </div>
          )}
          {children}
        </div>
      </dialog>
    </ModalPortal>
  );
};
